import { MouseEvent } from 'react';

import { TileListing } from '@nocowanie/noc-api';

import { IconsLinear } from '@nocowanie/icons/linear';
import { IconsNocowanie } from '@nocowanie/icons/nocowanie';

import { DistanceFrom } from '@app/components/atoms/distance-from';
import { AccommodationTileLocationProps } from '@app/components/molecules/accommodation-tile';
import { DistanceTargetType, LocationTypeEnum } from '@app/enums';
import { formatDistance } from '@app/helpers/distance.helpers';

import s from './accommodation-tile-lite.module.css';
import { TileIcon } from './tile-icon';

const MAX_DISPLAY_BEACH_DISTANCE_METERS = 3000;

type TileLocationProps = Omit<AccommodationTileLocationProps, 'isESI'>;

/** Same markup as `AccommodationTileLocation` (non-ESI), without Chakra runtime styling */
export const TileLocation = ({
    accommodation,
    searchCriteria = {},
    mapUrl,
    beachDistanceMeters,
    t,
    cityLocation,
    varietyGenitive,
}: TileLocationProps) => {
    const { listingPlacement, name, settlementName, isOTA, coordinates } = accommodation;

    const handleShowOnMap = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        window.location.href = mapUrl;
    };

    const hasDistanceTarget =
        !!searchCriteria.poiId || searchCriteria.locationType === LocationTypeEnum.City;
    const showBeachDistance =
        !!beachDistanceMeters && beachDistanceMeters <= MAX_DISPLAY_BEACH_DISTANCE_METERS;

    return (
        <div className={s.location}>
            <TileIcon icon={IconsLinear.Location} className={s.locationIcon} />
            <ul role={'list'} data-testid={'acc-tile-location'} className={s.locationList}>
                <li className={s.locationItem}>{settlementName}</li>
                {isOTA && (
                    <>
                        <li className={s.locationItemDesktop}>
                            <button
                                type={'button'}
                                aria-label={`${t('showOnMap')} ${name}`}
                                data-show-on-map-url={mapUrl}
                                onClick={handleShowOnMap}
                                className={s.showOnMap}
                            >
                                {t('showOnMap')}
                            </button>
                        </li>
                        {hasDistanceTarget && cityLocation && coordinates ? (
                            <li
                                className={
                                    showBeachDistance ? s.locationItemDesktop : s.locationItem
                                }
                            >
                                <DistanceFrom
                                    accommodationCoords={{
                                        lat: coordinates.lat,
                                        lon: coordinates.lon,
                                    }}
                                    targetCoords={cityLocation}
                                    targetType={DistanceTargetType.CityCenter}
                                    targetName={varietyGenitive}
                                    isNearby={listingPlacement === TileListing.placement.NEARBY}
                                />
                            </li>
                        ) : null}
                    </>
                )}
                {showBeachDistance ? (
                    <li className={s.locationItemBeach}>
                        <TileIcon icon={IconsNocowanie.Sea} className={s.beachIcon} />
                        {t('beachDistance', { distance: formatDistance(beachDistanceMeters) })}
                    </li>
                ) : null}
            </ul>
        </div>
    );
};
